import { Dialog, Transition } from "@headlessui/react";
import { SearchIcon, XCircleIcon } from "icons/outline";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Input } from "ui";
import { getAvailableProviders } from "../helpers/api";
import { Provider } from "../types/provider";
import { useMoneyHashQuery } from "../../../hooks/useMoneyHashQuery";
import { parseErrorsToArray } from "../../../utils/parseErrors";
import Loading from "../../../components/Loading";
import NoResults from "../../../components/NoResults/NoResults";
import ProviderCard from "../../../components/ProviderCard";
import { ListMeta } from "../../../components/Table/types";
import { InPlaceFilters } from "../../../components/newFilters";
import { isArray } from "../../form/utils/isArray";
import { useRouter } from "next/router";
import { usePlatformConnect } from "../../../contexts/PlatformConnectProvider";

type AvailableProvidersModalProps = {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
};

const platformConnectParams = [
  "currency",
  "mode",
  "countries",
  "exclude_available_providers",
  "filter_available_providers",
  "bulk_get_supported",
];

export const AvailableProvidersModal = ({
  showModal,
  setShowModal,
}: AvailableProvidersModalProps) => {
  const { account, fetchActiveConnectedProvider, isPlatformConnectMode } =
    usePlatformConnect();
  const router = useRouter();
  const { query } = router;

  const [searchKey, setSearchKey] = useState("");
  const [filterEnabled, setFilterEnabled] = useState<boolean | null>(null);
  const [filtersList, setFiltersList] = useState<ListMeta["filter_types"]>([]);
  const [appliedFilters, setAppliedFilters] = useState<Record<string, any>>({});

  const getListFilters = () => {
    return Object.keys(appliedFilters).reduce((acc, curr) => {
      acc[curr] = isArray(appliedFilters[curr])
        ? appliedFilters[curr].join(",")
        : appliedFilters[curr];
      return acc;
    }, {} as any);
  };

  const getPlatformConnectQueryParams = () => {
    return Object.keys(query).reduce((acc, key) => {
      if (platformConnectParams.includes(key)) {
        acc[key] = query[key];
      }

      return acc;
    }, {} as any);
  };

  const getApiQueryParams = () => {
    const filters = getListFilters();
    const { currency, ...platformConnectQueryParams } =
      getPlatformConnectQueryParams();

    return {
      ...platformConnectQueryParams,
      ...(isPlatformConnectMode && { currency }),
      ...filters,
    };
  };

  const {
    data: availableProviders = [],
    listMeta,
    isLoading,
    isError,
    isSuccess,
    error,
  } = useMoneyHashQuery<Provider[]>({
    queryKey: ["availableProviders", appliedFilters],
    queryFn: ({ signal }) => {
      const params = getApiQueryParams();
      return getAvailableProviders({ params, signal });
    },
    staleTime: Infinity,
  });

  const { count: activeConnectionsCount = 0 } = useMoneyHashQuery({
    queryKey: ["accountActiveConnectedProviders", (account as any)?.id],
    queryFn: fetchActiveConnectedProvider as any,
    enabled: !!account,
  });

  const isAboveActiveProviderLimit = useMemo(() => {
    if (!(account as any)?.max_active_provider_count) return false;

    const maxActiveProviders = (account as any)?.max_active_provider_count;
    return activeConnectionsCount >= maxActiveProviders;
  }, [account, activeConnectionsCount]);

  useEffect(() => {
    if (listMeta?.filter_enabled && !filterEnabled) {
      setFilterEnabled(true);
      setFiltersList(listMeta?.filter_types);
    }
  }, [filterEnabled, listMeta]);

  const filteredProviders = useMemo(() => {
    if (!searchKey) return availableProviders;

    return availableProviders?.filter((provider) =>
      provider.payName
        .toLowerCase()
        .includes((searchKey as string).toLowerCase())
    );
  }, [availableProviders, searchKey]);

  const onCloseModal = () => {
    setShowModal(false);
    setSearchKey("");

    const { type: _, ...restQueryParams } = router.query;

    router.replace({
      query: {
        ...restQueryParams,
      },
    });
  };

  const searchInput = useRef(null);

  return (
    <Transition.Root show={showModal} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-10 overflow-y-auto"
        initialFocus={searchInput}
        onClose={onCloseModal}
      >
        <div className="flex min-h-screen items-end justify-center px-4 pb-20 pt-4 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-foundations-subtler/50 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:h-screen sm:align-middle"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="relative z-10 inline-block overflow-hidden rounded-lg bg-foundations-default  text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-6xl sm:align-middle">
              <header className="flex flex-col gap-4 border-b border-b-foundations-subtle px-12 pb-4 pt-6">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-foundations-bolder">
                    Add new connection
                  </h3>

                  <button>
                    <XCircleIcon
                      className="h-5 w-5 text-foundations-subtle"
                      onClick={onCloseModal}
                    />
                  </button>
                </div>

                <div className="flex flex-wrap gap-4">
                  <Input
                    ref={searchInput}
                    className="w-72"
                    name="searchProviders"
                    value={searchKey}
                    onChange={(value) => {
                      setSearchKey(value);
                    }}
                    icon={<SearchIcon />}
                    placeholder="Search"
                    autoComplete="off"
                  />

                  {!isPlatformConnectMode && filterEnabled && (
                    <InPlaceFilters
                      filters={filtersList}
                      showLabel={false}
                      showClearFilters={false}
                      appliedFilters={appliedFilters}
                      customApplyFilter={(filter: string, value: any) => {
                        setAppliedFilters((prev) => ({
                          ...prev,
                          [filter]: value,
                        }));
                      }}
                      customRemoveFilter={(filter: string) => {
                        const { [filter]: _, restFilters } = appliedFilters;
                        setAppliedFilters({ ...restFilters });
                      }}
                      customClearFilters={() => setAppliedFilters({})}
                    />
                  )}
                </div>
              </header>

              <div className="h-[500px] overflow-auto px-12 py-6">
                {isLoading ? (
                  <Loading />
                ) : isError ? (
                  <div className="my-10" data-testid="providers-error">
                    {parseErrorsToArray(error)?.[0]}
                  </div>
                ) : isSuccess ? (
                  <ProvidersList
                    providersList={filteredProviders}
                    searchKey={searchKey}
                    setSearchKey={setSearchKey}
                    setAppliedFilters={setAppliedFilters}
                    isAboveActiveProviderLimit={isAboveActiveProviderLimit}
                    currency={appliedFilters.currency}
                  />
                ) : null}
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

const ProvidersList = ({
  providersList,
  searchKey,
  setSearchKey,
  setAppliedFilters,
  isAboveActiveProviderLimit,
  currency,
}: {
  providersList: Provider[];
  searchKey: string;
  setSearchKey: React.Dispatch<React.SetStateAction<string>>;
  setAppliedFilters: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  isAboveActiveProviderLimit: boolean;
  currency: string | null;
}) => {
  return (
    <>
      {!!providersList.length ? (
        <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {providersList.map((provider, index) => (
            <ProviderCard
              key={`${provider.id}-${index}`}
              provider={provider}
              isAboveActiveProviderLimit={isAboveActiveProviderLimit}
              currency={currency}
            />
          ))}
        </div>
      ) : (
        <NoResults
          isFiltering={!!searchKey}
          tableName="providers"
          removeAllFilters={() => {
            setSearchKey("");
            setAppliedFilters({});
          }}
        />
      )}
    </>
  );
};
